
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import type { Language, ApiLogEntry } from './types';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { MainSearch } from './components/MainSearch';
import { SearchResults } from './components/SearchResults';
import { HotelDetail } from './components/HotelDetail';
import { BookingPage } from './components/BookingPage';
import { PaymentPage } from './components/PaymentPage';
import { ConfirmationPage } from './components/ConfirmationPage';
import { SettingsPanel } from './components/SettingsPanel';
import { ApiLogViewer } from './components/ApiLogViewer';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { HolidaysPage } from './components/HolidaysPage';
import { FlightsPage } from './components/landing/FlightsPage';
import { DestinationLandingPage } from './components/DestinationLandingPage';
import { LocalizationContext, getLocalizationDefaults } from './contexts/LocalizationContext';
import { useLocalization } from './hooks/useLocalization';
import { getWidgetSettings } from './services/dataService';
import { GlobalChatWidget } from './components/GlobalChatWidget';
import { CookieConsent } from './components/CookieConsent';
import { AuthPage } from './components/AuthPage';
import { FlightSelection } from './components/FlightSelection';
import { AboutPage } from './components/AboutPage';
import { AuthProvider } from './contexts/AuthContext';
import { AdminThemeProvider } from './contexts/AdminThemeContext';
import { MyBookingsPage } from './components/MyBookingsPage';
import BookingDetail from './components/user/BookingDetail';
import { ProfilePage } from './components/ProfilePage';
import TourAssistPage from './components/TourAssistPage';
import GoogleAnalytics, { CookieConsentBanner } from './components/GoogleAnalytics';
import StaticPage from './components/StaticPage';
import NotFoundPage from './components/NotFoundPage';
import ProSearchPage from './components/ProSearchPage';
import HotelLandingPage from './components/HotelLandingPage';
import { HotelsPage } from './components/landing/HotelsPage';
import { RestaurantPage } from './components/RestaurantPage';
import { BlogListPage, BlogDetailPage } from './components/BlogPage';

// Pageview & Behavior Tracker - tracks real visitor data for analytics
import { behaviorTracker } from './services/behaviorTracker';

const PageviewTracker = () => {
  const location = useLocation();
  const { language } = useLocalization();

  useEffect(() => {
    // Skip admin routes to avoid self-tracking
    if (location.pathname.startsWith('/admin')) return;

    const pageUrl = location.pathname + location.search;

    // Track pageview (fires to /api/analytics/track)
    behaviorTracker.trackPageView(pageUrl, language);

    // Start behavior tracking (scroll, clicks, time) for this page
    behaviorTracker.startPage(pageUrl);

    return () => {
      // Flush behavior data when navigating away
      behaviorTracker.stopPage();
    };
  }, [location.pathname, location.search, language]);

  return null;
};

const CampaignBanner = () => {
  const { t } = useLocalization();
  const [settings, setSettings] = useState<any>({ campaignActive: false });

  // Listen for real-time updates from Admin Panel
  useEffect(() => {
    const loadSettings = async () => setSettings(await getWidgetSettings());
    loadSettings(); // Initial load

    const updateSettings = () => { loadSettings(); };

    window.addEventListener('tourvisio_data_update', updateSettings);
    window.addEventListener('storage', updateSettings); // Sync across tabs

    return () => {
      window.removeEventListener('tourvisio_data_update', updateSettings);
      window.removeEventListener('storage', updateSettings);
    };
  }, []);

  // Logic to check activation and dates
  if (settings.campaignActive === false) return null;

  const now = new Date();
  if (settings.campaignStartDate) {
    const start = new Date(settings.campaignStartDate);
    if (now < start) return null;
  }
  if (settings.campaignEndDate) {
    const end = new Date(settings.campaignEndDate);
    // Set end date to end of day
    end.setHours(23, 59, 59, 999);
    if (now > end) return null;
  }

  const bgColor = settings.campaignBgColor || '#f59e0b'; // Default amber-500
  const textColor = settings.campaignTextColor || '#ffffff';

  // Character Limit Logic
  const rawText = settings.campaignText || t('campaign_banner_text');
  const CHAR_LIMIT = 80;
  const displayText = rawText.length > CHAR_LIMIT ? rawText.substring(0, CHAR_LIMIT) + '...' : rawText;

  return (
    <div
      className="min-h-[40px] py-1.5 flex items-center justify-center text-xs md:text-sm font-bold shadow-md w-full relative z-[60] px-4 text-center transition-all duration-300"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <p className="animate-pulse leading-tight break-words max-w-full">
        {displayText}
      </p>
    </div>
  );
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('lumi_admin_language');
    return (saved === 'en' || saved === 'sv') ? saved : 'sv';
  });
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('lumi_admin_language', lang);
  }, []);
  const [showSettings, setShowSettings] = useState(false);
  const [apiLogs, setApiLogs] = useState<ApiLogEntry[]>([]);
  const [isSettingsReady, setIsSettingsReady] = useState(false);
  const [isApiLogOpen, setIsApiLogOpen] = useState(false);

  const localizationContextValue = useMemo(() => {
    const defaults = getLocalizationDefaults(language);
    return { 
      language, 
      ...defaults,
      setLanguage 
    };
  }, [language, setLanguage]);

  useEffect(() => {
    const settings = localStorage.getItem('tourvisioApiSettings');
    if (!settings) {
      const defaultSettings = {
        serviceAddress: 'https://b2cservice.lumitours.com',
        agency: 'lumiweb',
        user: 'lumiwebus',
        password: '1',
      };
      localStorage.setItem('tourvisioApiSettings', JSON.stringify(defaultSettings));
    }
    setIsSettingsReady(true);
    console.log('🚀 LumiTours Frontend v1.1.0 Loaded', new Date().toISOString());
  }, []);

  // Global error catcher — logs visitor errors to /api/visitor-errors
  useEffect(() => {
    const recentErrors = new Set<string>();

    const sendError = (payload: any) => {
      // Skip admin pages and debounce repeated errors
      if (window.location.pathname.startsWith('/admin')) return;
      const key = `${payload.type}:${payload.message}`;
      if (recentErrors.has(key)) return;
      recentErrors.add(key);
      setTimeout(() => recentErrors.delete(key), 5000);

      fetch('/api/visitor-errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, pageUrl: window.location.href })
      }).catch(() => { }); // Don't fail silently
    };

    const handleError = (event: ErrorEvent) => {
      sendError({
        message: event.message || 'Unknown error',
        stack: event.error?.stack,
        type: 'js_error',
        metadata: { filename: event.filename, lineno: event.lineno, colno: event.colno }
      });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      sendError({
        message: reason?.message || String(reason) || 'Unhandled promise rejection',
        stack: reason?.stack,
        type: 'unhandled_rejection'
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    // Also monkey-patch fetch to capture API errors
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      try {
        const res = await originalFetch(...args);
        if (!res.ok && res.status >= 500) {
          const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || '';
          if (!url.includes('/api/visitor-errors')) {
            sendError({
              message: `API Error: ${res.status} ${res.statusText}`,
              type: 'api_error',
              url,
              method: (args[1] as RequestInit)?.method || 'GET',
              statusCode: res.status
            });
          }
        }
        return res;
      } catch (err: any) {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || '';
        if (!url.includes('/api/visitor-errors')) {
          sendError({
            message: err?.message || 'Network request failed',
            type: 'network_error',
            url,
            method: (args[1] as RequestInit)?.method || 'GET'
          });
        }
        throw err;
      }
    };

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
      window.fetch = originalFetch;
    };
  }, []);

  const logApiCall = useCallback((log: ApiLogEntry) => {
    setApiLogs(prevLogs => [log, ...prevLogs.slice(0, 49)]);
  }, []);

  const handleCloseSettings = () => {
    const settings = localStorage.getItem('tourvisioApiSettings');
    if (settings) {
      setIsSettingsReady(true);
      setShowSettings(false);
    }
  };

  return (
    <AuthProvider>
      <LocalizationContext.Provider value={localizationContextValue}>
        <BrowserRouter>
          <GoogleAnalytics />
          <CookieConsentBanner />
          <ScrollToTop />
          <PageviewTracker />
          <div className="flex flex-col min-h-screen bg-gray-50 font-sans text-gray-900">
            <Routes>

              {/* Admin Route - Allow sub-paths */}
              <Route path="/admin/*" element={
                <AdminThemeProvider>
                  <AdminDashboard logs={apiLogs} logApiCall={logApiCall} />
                </AdminThemeProvider>
              } />

              {/* Main App Routes */}
              <Route path="*" element={
                <div className="flex flex-col flex-grow">
                  <CampaignBanner />
                  <Header language={language} setLanguage={setLanguage} onShowSettings={() => setShowSettings(true)} />
                  <main className="flex-grow">
                    {showSettings ? (
                      <SettingsPanel onClose={handleCloseSettings} />
                    ) : (
                      <Routes>
                        <Route path="/" element={<MainSearch language={language} logApiCall={logApiCall} isSettingsReady={isSettingsReady} />} />
                        <Route path="/destination/:slug" element={<DestinationLandingPage logApiCall={logApiCall} isSettingsReady={isSettingsReady} />} />
                        <Route path="/holidays" element={<HolidaysPage logApiCall={logApiCall} isSettingsReady={isSettingsReady} />} />
                        <Route path="/flights" element={<FlightsPage logApiCall={logApiCall} isSettingsReady={isSettingsReady} />} />
                        <Route path="/results" element={<SearchResults logApiCall={logApiCall} />} />
                        <Route path="/hotel/:destination/:slug" element={<HotelDetail logApiCall={logApiCall} />} />
                        <Route path="/hotel/:id" element={<HotelDetail logApiCall={logApiCall} />} />
                        <Route path="/hotels/:slug" element={<HotelLandingPage />} />
                        <Route path="/onlyhotel" element={<HotelsPage logApiCall={logApiCall} isSettingsReady={isSettingsReady} />} />
                        <Route path="/restaurants/:slug" element={<RestaurantPage />} />
                        <Route path="/destination/:slug/blog" element={<BlogListPage />} />
                        <Route path="/destination/:slug/blog/:postSlug" element={<BlogDetailPage />} />
                        <Route path="/blog/:slug" element={<BlogListPage />} />
                        <Route path="/blog/:slug/:postSlug" element={<BlogDetailPage />} />
                        <Route path="/booking" element={<BookingPage logApiCall={logApiCall} />} />
                        <Route path="/payment" element={<PaymentPage logApiCall={logApiCall} />} />
                        <Route path="/confirmation" element={<ConfirmationPage />} />
                        <Route path="/login" element={<AuthPage mode="login" />} />
                        <Route path="/register" element={<AuthPage mode="register" />} />
                        <Route path="/my-bookings" element={<MyBookingsPage />} />
                        <Route path="/bookings" element={<MyBookingsPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/user/bookings/:id" element={<BookingDetail logApiCall={logApiCall} />} />
                        <Route path="/flight-selection" element={<FlightSelection logApiCall={logApiCall} />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/search-pro" element={<ProSearchPage logApiCall={logApiCall} isSettingsReady={isSettingsReady} />} />
                        <Route path="/tour-assist" element={<TourAssistPage />} />
                        <Route path="/sv/terms-and-conditions" element={<StaticPage pageId="terms-and-conditions" language="sv" />} />
                        <Route path="/sv/cookie-gdpr" element={<StaticPage pageId="cookie-gdpr" language="sv" />} />
                        <Route path="/sv/cancellation" element={<StaticPage pageId="cancellation" language="sv" />} />
                        <Route path="/en/terms-and-conditions" element={<StaticPage pageId="terms-and-conditions" language="en" />} />
                        <Route path="/en/cookie-gdpr" element={<StaticPage pageId="cookie-gdpr" language="en" />} />
                        <Route path="/en/cancellation" element={<StaticPage pageId="cancellation" language="en" />} />
                        <Route path="/da/terms-and-conditions" element={<StaticPage pageId="terms-and-conditions" language="da" />} />
                        <Route path="/da/cookie-gdpr" element={<StaticPage pageId="cookie-gdpr" language="da" />} />
                        <Route path="/da/cancellation" element={<StaticPage pageId="cancellation" language="da" />} />
                        <Route path="/sv/about" element={<AboutPage />} />
                        <Route path="/en/about" element={<AboutPage />} />
                        <Route path="/da/about" element={<AboutPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                      </Routes>
                    )}
                  </main>
                  <ApiLogViewer logs={apiLogs} isOpen={isApiLogOpen} onClose={() => setIsApiLogOpen(false)} />
                  {/* Global Chat Widget - Siyah butonlu, tüm özellikleriyle */}
                  <GlobalChatWidget />
                  <CookieConsent />
                  <Footer onToggleLogs={() => setIsApiLogOpen(true)} language={language} setLanguage={setLanguage} />
                </div>
              } />
            </Routes>
          </div>
        </BrowserRouter>
      </LocalizationContext.Provider>
    </AuthProvider>
  );
};

export default App;
